import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n, type Language, type TranslationKey } from '../i18n/I18nProvider';
import { useGame } from '../context/GameContext';
import { useTheme } from '../theme/ThemeProvider';
import { isValidThemeId } from '../theme/themes';
import { useSound } from '../context/SoundContext';
import { useLayout } from '../layout/useLayout';
import { getScreenLayout, resolveContentWidthClass, type LayoutScreenKey } from '../layout/layouts';
import { useTypography } from '../typography/useTypography';
import BuildVersionTag from './BuildVersionTag';
import { useBackNavigation } from '../context/BackNavigationContext';
import { loadGroupToken } from '../utils/groupPersistence';
import { buildOnlineShareLink, buildRealShareLink } from '../utils/appLinks';
import { shareLink } from '../utils/shareLink';
import { FloatingPanelCard } from '../layout/regions/FloatingPanelCard';
import { StyledSelect } from './StyledSelect';

function isLanguage(value: string): value is Language {
  return value === 'pl' || value === 'en';
}

function getFloatingScreenKey(pathname: string, search: string): LayoutScreenKey {
  if ((pathname === '/online' || pathname === '/online/') && new URLSearchParams(search).get('view') === 'results') {
    return 'gameOver';
  }
  if (pathname === '/') return 'home';
  if (pathname === '/online' || pathname === '/online/') return 'lobby';
  if (pathname === '/real' || pathname === '/real/') return 'lobby';
  if (pathname.startsWith('/real/host/')) return 'host';
  if (pathname.startsWith('/real/join') || pathname.startsWith('/online/join')) return 'join';
  if (pathname.startsWith('/real/game-over') || pathname.startsWith('/online/game-over')) return 'gameOver';
  if (pathname.includes('/results/')) return 'gameResults';
  if (pathname.endsWith('/scores') || pathname === '/scores' || pathname.includes('/account/results')) return 'scores';
  if (pathname.endsWith('/stats') || pathname === '/stats') return 'stats';
  if (pathname.endsWith('/guide') || pathname === '/guide') return 'guide';
  if (pathname.endsWith('/privacy') || pathname.endsWith('/terms') || pathname === '/privacy' || pathname === '/terms') return 'legal';
  if (pathname.startsWith('/real/game') || pathname.startsWith('/online/game/')) return 'game';
  return 'home';
}

export default function FloatingControls() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, languages, setLanguage } = useI18n();
  const { themeId, themes, setTheme } = useTheme();
  const { layoutId } = useLayout();
  const { typographyId, typographyList, setTypography } = useTypography();
  const { soundEnabled, toggleSound, hapticsEnabled, toggleHaptics } = useSound();
  const { handler: backHandler } = useBackNavigation();
  const { gameState } = useGame();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);
  const openButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const lastFocusRef = React.useRef<HTMLElement | null>(null);
  const lastPointerToggleAtRef = React.useRef(0);

  const isHome = location.pathname === '/';
  const isActiveOnlineGame = location.pathname.startsWith('/online/game/');
  const isRealLobby = location.pathname === '/real' || location.pathname === '/real/';
  const isRealHost = location.pathname.startsWith('/real/host/');
  const isRealGame = location.pathname.startsWith('/real/game/');
  const isRealFlow = isRealLobby || isRealHost || isRealGame;
  const isScoresView = /^\/(?:real\/|online\/)?scores(?:\/|$)/.test(location.pathname) || /\/account\/results(?:\/|$)/.test(location.pathname);
  const controlsInsetClass = isScoresView ? 'px-2' : 'px-3';
  const floatingScreenKey = React.useMemo(
    () => getFloatingScreenKey(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const floatingScreenLayout = React.useMemo(() => getScreenLayout(layoutId, floatingScreenKey), [layoutId, floatingScreenKey]);
  const floatingControlsMaxWidthClass = React.useMemo(
    () => resolveContentWidthClass(floatingScreenLayout.contentWidth),
    [floatingScreenLayout.contentWidth],
  );
  const floatingControlsContainerStyle = React.useMemo(
    () => (
      floatingControlsMaxWidthClass
        ? undefined
        : ({ maxWidth: 'var(--layout-content-max-width)' } as React.CSSProperties)
    ),
    [floatingControlsMaxWidthClass],
  );
  const routeOnlineGameId = React.useMemo(() => {
    const match = location.pathname.match(/^\/online\/game\/([^/?#]+)/i);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]).toUpperCase();
    } catch {
      return match[1].toUpperCase();
    }
  }, [location.pathname]);
  const floatingGameId = isActiveOnlineGame
    ? (routeOnlineGameId || (gameState.id ? gameState.id.toUpperCase() : null))
    : null;
  const groupToken = gameState.mode === 'ONLINE' ? (gameState.groupToken || loadGroupToken()) : null;
  const gameLink = gameState.mode === 'ONLINE' && (groupToken || gameState.id)
    ? buildOnlineShareLink(groupToken, gameState.id)
    : (gameState.mode === 'REAL' && gameState.id ? buildRealShareLink(gameState.id) : '');
  const canCopyGameLink = Boolean(gameLink) && (
    (gameState.mode === 'ONLINE' && isActiveOnlineGame)
    || (gameState.mode === 'REAL' && isRealFlow)
  );
  const floatingControlsStyle = React.useMemo(
    () => ({ bottom: 'calc(1rem + var(--app-safe-area-bottom))' } as React.CSSProperties),
    []
  );
  const copyGameLink = React.useCallback(async () => {
    if (!gameLink) return;
    try {
      const method = await shareLink({ url: gameLink });
      if (method === 'clipboard') {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 1500);
      }
    } catch {
      // ignore clipboard errors
    }
  }, [gameLink]);

  const goBack = React.useCallback(() => {
    if (backHandler) {
      backHandler();
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [backHandler, navigate]);

  React.useEffect(() => {
    setSettingsOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const mainEl = document.getElementById('main');
    if (settingsOpen) {
      lastFocusRef.current = document.activeElement as HTMLElement | null;
      requestAnimationFrame(() => closeButtonRef.current?.focus());
      if (mainEl) {
        mainEl.setAttribute('inert', '');
      }
      return;
    }
    if (mainEl) {
      mainEl.removeAttribute('inert');
    }
    const target = openButtonRef.current ?? lastFocusRef.current;
    if (target) {
      requestAnimationFrame(() => target.focus());
    }
  }, [settingsOpen]);

  const handleThemeChange = React.useCallback((nextThemeId: string) => {
    if (isValidThemeId(nextThemeId)) {
      setTheme(nextThemeId);
    }
  }, [setTheme]);

  const handleTypographyChange = React.useCallback((nextId: string) => {
    setTypography(nextId as typeof typographyId);
  }, [setTypography]);

  const handleLanguageChange = React.useCallback((nextLanguage: string) => {
    if (isLanguage(nextLanguage)) {
      setLanguage(nextLanguage);
    }
  }, [setLanguage]);

  const toggleSettingsOpen = React.useCallback(() => {
    setSettingsOpen((value) => !value);
  }, []);

  const handleSettingsPointerUp = React.useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    lastPointerToggleAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    toggleSettingsOpen();
  }, [toggleSettingsOpen]);

  const handleSettingsClick = React.useCallback(() => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - lastPointerToggleAtRef.current < 250) return;
    toggleSettingsOpen();
  }, [toggleSettingsOpen]);

  return (
    <>
      <div className={`fixed inset-x-0 z-40 ${controlsInsetClass} pointer-events-none`} style={floatingControlsStyle}>
        <div
          className={`floating-controls__shell mx-auto w-full ${floatingControlsMaxWidthClass ?? ''}`.trim()}
          style={floatingControlsContainerStyle}
        >
          <div className="floating-controls__row relative flex items-end justify-between">
            <div
              style={{ width: 'var(--floating-fab-size)', height: 'var(--floating-fab-size)' }}
            >
              {!isHome && (
                <button
                  type="button"
                  onClick={goBack}
                  className="floating-fab pointer-events-auto flex items-center justify-center rounded-full border border-brand-border bg-(--color-bg-alt) text-accent shadow-[0_16px_30px_rgba(0,0,0,0.35)] transition hover:-translate-y-0.5 focus-visible:translate-y-0 focus-visible:outline-hidden"
                  aria-label={t('settings.backAria')}
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 5l-7 7 7 7" />
                  </svg>
                </button>
              )}
            </div>

            {floatingGameId && (
              <div className="absolute left-1/2 bottom-1 -translate-x-1/2">
                <div
                  className="floating-game-badge rounded-full border border-brand-border bg-(--color-bg-alt)/92 px-3 py-1 shadow-[0_14px_30px_rgba(0,0,0,0.32)] backdrop-blur-md"
                  data-testid="floating-online-game-id"
                >
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-brand-text/70">{t('game.floatingId')}</span>
                    <span className="text-[13px] font-semibold text-accent">{floatingGameId}</span>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              onPointerUp={handleSettingsPointerUp}
              onClick={handleSettingsClick}
              ref={openButtonRef}
              className={`floating-fab pointer-events-auto flex items-center justify-center rounded-full text-(--color-on-accent) shadow-[0_16px_30px_rgba(0,0,0,0.35)] transition hover:-translate-y-0.5 focus-visible:translate-y-0 focus-visible:outline-hidden ${
                settingsOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
              style={{ background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-strong) 100%)' }}
              aria-label={t('settings.openAria')}
              aria-expanded={settingsOpen}
              aria-controls="settings-panel"
              tabIndex={settingsOpen ? -1 : 0}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .68.39 1.3 1 1.58.31.14.65.21 1 .21H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {settingsOpen && (
        <>
          <div
            className="fixed inset-0 z-200000 bg-(--color-bg)/28 backdrop-blur-md"
            onClick={() => setSettingsOpen(false)}
            aria-hidden="true"
          />
          <div className={`fixed inset-x-0 z-200010 ${controlsInsetClass} pointer-events-none`} style={floatingControlsStyle}>
            <div
              className={`floating-controls__shell mx-auto flex w-full justify-end ${floatingControlsMaxWidthClass ?? ''}`.trim()}
              style={floatingControlsContainerStyle}
            >
              <div className="w-[92vw] max-w-[320px] pointer-events-auto lg:max-w-[360px]">
                <FloatingPanelCard
                  id="settings-panel"
                  className="settings-panel"
                  style={{
                    backgroundColor: 'var(--color-bg-alt)',
                    background:
                      'linear-gradient(160deg, color-mix(in srgb, var(--color-bg-alt) 86%, var(--color-accent) 14%), color-mix(in srgb, var(--color-bg-alt) 86%, var(--color-bg) 14%))',
                  }}
                  role="dialog"
                  aria-label={t('settings.title')}
                >
            <div className="settings-panel__header mb-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-brand-text/60">{t('settings.kicker')}</p>
                <h2 className="text-lg font-semibold text-accent">{t('settings.title')}</h2>
              </div>
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                ref={closeButtonRef}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-border bg-(--color-bg) text-brand-text/70 transition hover:text-accent"
                aria-label={t('settings.closeAria')}
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="M6 6 18 18" />
                </svg>
              </button>
            </div>

            <div className="settings-panel__body space-y-3">
              {canCopyGameLink && (
                <div className="settings-panel__block rounded-xl border border-brand-border bg-(--color-bg)/70 p-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-brand-text/60 mb-2">
                    {t('settings.gameLinkLabel')}
                  </p>
                  <button
                    type="button"
                    onClick={copyGameLink}
                    className="w-full rounded-lg border border-brand-border bg-(--color-bg) px-3 py-2 text-left text-xs text-(--color-text) transition active:scale-[0.98]"
                    title={gameLink}
                    data-testid="quick-copy-game-link"
                  >
                    {linkCopied ? t('settings.gameLinkCopied') : t('settings.gameLinkCopy')}
                  </button>
                </div>
              )}
              <div className="settings-panel__toggle flex items-center justify-between gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium">{t('settings.soundLabel')}</label>
                  <p className="text-[10px] opacity-70 mt-1">{t('settings.soundHint')}</p>
                </div>
                <button
                  type="button"
                  onClick={toggleSound}
                  className={`ml-2 relative inline-flex h-8 w-14 items-center rounded-full border transition-all duration-200 ${
                    soundEnabled
                      ? 'bg-(--color-accent) border-(--color-accent) shadow-inner'
                      : 'bg-(--color-bg-alt) border-(--color-border)'
                  }`}
                  aria-pressed={soundEnabled}
                  aria-label={t('settings.soundAria')}
                >
                  <span
                    className={`h-6 w-6 rounded-full bg-white shadow transition-transform duration-200 ${
                      soundEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="settings-panel__toggle flex items-center justify-between gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium">{t('settings.hapticsLabel')}</label>
                  <p className="text-[10px] opacity-70 mt-1">{t('settings.hapticsHint')}</p>
                </div>
                <button
                  type="button"
                  onClick={toggleHaptics}
                  className={`ml-2 relative inline-flex h-8 w-14 items-center rounded-full border transition-all duration-200 ${
                    hapticsEnabled
                      ? 'bg-(--color-accent) border-(--color-accent) shadow-inner'
                      : 'bg-(--color-bg-alt) border-(--color-border)'
                  }`}
                  aria-pressed={hapticsEnabled}
                  aria-label={t('settings.hapticsAria')}
                >
                  <span
                    className={`h-6 w-6 rounded-full bg-white shadow transition-transform duration-200 ${
                      hapticsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="settings-panel__field">
                <label className="text-xs font-medium">{t('settings.themeLabel')}</label>
                <StyledSelect
                  ariaLabel={t('theme.select')}
                  value={themeId}
                  onChange={handleThemeChange}
                  options={themes.map(theme => ({ value: theme.id, label: t(`theme.name.${theme.id}` as TranslationKey) }))}
                  className="mt-2"
                  testId="theme-select"
                />
              </div>

              <div className="settings-panel__field">
                <label className="text-xs font-medium">{t('settings.typographyLabel')}</label>
                <StyledSelect
                  ariaLabel={t('settings.typographyLabel')}
                  value={typographyId}
                  onChange={handleTypographyChange}
                  options={typographyList.map(option => ({ value: option.id, label: t(`typography.name.${option.id}` as TranslationKey) }))}
                  className="mt-2"
                  testId="typography-select"
                />
              </div>

              <div className="settings-panel__field">
                <label className="text-xs font-medium">{t('settings.languageLabel')}</label>
                <StyledSelect
                  ariaLabel={t('lang.select')}
                  value={language}
                  onChange={handleLanguageChange}
                  options={languages.map(lang => ({ value: lang.id, label: lang.label }))}
                  className="mt-2"
                  testId="language-select"
                />
              </div>
            </div>

            <BuildVersionTag className="mt-4 text-right" />
                </FloatingPanelCard>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
